<template>
  <div class="seminar-assignment-event-fi">
    <div class="seminar-assignment-event-fi__head">
      <div class="status-label">
        <div
          :class="[
            'event-status',
            `event-status--${eventStatus(event.maxParticipants, event.actualParticipants)}`,
          ]"
        >
          <div class="event-status__tooltip">
            <div
              :class="[
                'event-status',
                `event-status--${eventStatus(event.maxParticipants, event.actualParticipants)}`,
              ]"
            />
            {{ eventStatusText(eventStatus(event.maxParticipants, event.actualParticipants)) }}
          </div>
        </div>

        <span v-if="fields.seminarCounterLabel?.value">
          {{ fields.seminarCounterLabel.value.replace('{0}', String(idx + 1)) }}
        </span>
      </div>

      <div class="date">
        <template v-if="!event.followEvents">
          <div class="date__info">
            <div class="seminar-assignment-event-fi__icon-value">
              <icon-fi
                icon="calendar"
                class="seminar-assignment-event-fi__icon-value__icon"
              />

              <span
                v-if="event.start.day"
                class="seminar-assignment-event-fi__icon-value__value"
              >
                {{ event.start.day }}
              </span>
            </div>
            <div class="seminar-assignment-event-fi__icon-value">
              <icon-fi
                icon="clock"
                class="seminar-assignment-event-fi__icon-value__icon"
              />
              <span
                v-if="event.start.time && event.end.time"
                class="seminar-assignment-event-fi__icon-value__value"
              >
                {{ event.start.time }} - {{ event.end.time }}
              </span>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="seminar-assignment-event-fi__icon-value">
            <icon-fi
              icon="calendar"
              class="seminar-assignment-event-fi__icon-value__icon"
            />
            <span
              v-if="event.followEvents[0].start.day"
              class="seminar-assignment-event-fi__icon-value__value"
            >
              {{ event.followEvents[0].start.day }} -
              {{ event.followEvents[event.followEvents.length - 1].end.day }}
            </span>
          </div>
        </template>

        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="seminarDeadlineLabel(event)"
          class="date__deadline"
          v-html="seminarDeadlineLabel(event)"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>

      <div class="location-price">
        <div class="location">
          <div class="seminar-assignment-event-fi__icon-value location__info">
            <icon-fi
              icon="map-pin"
              class="seminar-assignment-event-fi__icon-value__icon"
            />
            <span class="seminar-assignment-event-fi__icon-value__value">
              {{
                event.location.type === 'onsite'
                  ? event.location.city
                  : $dict.get('seminars.Location.Online')
              }}
            </span>
          </div>
        </div>
        <div class="price">
          <div class="seminar-assignment-event-fi__icon-value">
            <icon-fi
              icon="money"
              class="seminar-assignment-event-fi__icon-value__icon"
            />
            <span class="seminar-assignment-event-fi__icon-value__value">{{ event.price }}</span>
          </div>
        </div>
      </div>
      <div class="action">
        <button-fi
          class="button-fi__primary"
          data-test="register-seminar"
          :target="hasExternalDescription ? '_blank' : null"
          :href="hasExternalDescription ? event.externalDescription : null"
          :rel="hasExternalDescription ? 'opener' : undefined"
          :natural-events="hasExternalDescription"
          @click="hasExternalDescription ? null : $emit('register')"
        >
          {{ fields.registerSeminar?.value }}
        </button-fi>
      </div>

      <div
        v-if="event.followEvents"
        class="toggle"
        @click="showEventBody = !showEventBody"
      >
        <div :class="{ 'toggle-details': true, 'toggle-details--active': showEventBody }">
          <icon-fi
            icon="chevron-down"
            class="toggle-details__icon"
          />
          {{ fields.toggleDetails?.value }}
        </div>
      </div>
    </div>

    <div
      :class="{
        'seminar-assignment-event-fi__body': true,
        'seminar-assignment-event-fi__body--expanded': showEventBody,
      }"
    >
      <div class="seminar-assignment-event-fi__body__inner">
        <div class="seminar-assignment-event-fi__details">
          <div class="date">
            <span v-if="fields.seminarDateLabel?.value">{{ fields.seminarDateLabel.value }}</span>
          </div>
          <div class="follow-events">
            <div
              v-for="eventDay in event.followEvents"
              :key="eventDay.id"
              class="follow-event"
            >
              <template v-if="eventDay.start">
                <div class="seminar-assignment-event-fi__icon-value">
                  <icon-fi
                    icon="calendar"
                    class="seminar-assignment-event-fi__icon-value__icon"
                  />
                  <span class="seminar-assignment-event-fi__icon-value__value">
                    {{ eventDay.start.day }}
                  </span>
                </div>
                <div class="seminar-assignment-event-fi__icon-value">
                  <icon-fi
                    icon="clock"
                    class="seminar-assignment-event-fi__icon-value__icon"
                  />
                  <span class="seminar-assignment-event-fi__icon-value__value">
                    {{ eventDay.start.time }} - {{ eventDay.end.time }}
                  </span>
                </div>
              </template>
              <div class="location">
                <div class="seminar-assignment-event-fi__icon-value">
                  <icon-fi
                    icon="map-pin"
                    class="seminar-assignment-event-fi__icon-value__icon"
                  />
                  <span class="seminar-assignment-event-fi__icon-value__value">
                    {{
                      event.location.type === 'onsite'
                        ? event.location.city
                        : $dict.get('seminars.Location.Online')
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ButtonFi, IconFi } from 'atoms';
import { computed, ref } from 'vue';

import { useFields } from '@/composables/fields';

defineOptions({
  name: 'SeminarAssignmentEventFi',
});

const fields = useFields('SeminarsOverviewFiFields');

interface Props {
  event?: any;
  idx?: number;
}

const props = withDefaults(defineProps<Props>(), {
  event: null,
  idx: 0,
});

defineEmits<{
  register: [value: void];
}>();

const showEventBody = ref<boolean>(false);

const hasExternalDescription = computed(() => Boolean(props.event?.externalDescription));

const eventStatus = (maxParticipants: number, actualParticipants: number) => {
  if (actualParticipants === maxParticipants) {
    return 'full';
  }

  if ((100 * actualParticipants) / maxParticipants > 25) {
    return 'partly';
  }

  return 'free';
};

const eventStatusText = (eventStatus: string) => {
  if (eventStatus === 'full') {
    return fields.seminarStatusFull?.value;
  }

  if (eventStatus === 'partly') {
    return fields.seminarStatusPartly?.value;
  }

  return fields.seminarStatusFree?.value;
};

const seminarDeadlineLabel = (event: any) => {
  if (fields.seminarDeadlineLabel?.value) {
    return fields.seminarDeadlineLabel.value.replace(
      '{0}',
      `<span class="fi-semibold">${
        event.location.type === 'onsite' ? event.deadline : event.start.day
      }</span>`,
    );
  }
  return null;
};
</script>

<style scoped src="./seminar-assignment-event-fi" />
